import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Check, ChevronDown, MapPin, Phone, Clock } from "lucide-react";
import { SITE, whatsappLink } from "@/data/site";
import { czk } from "@/lib/price";
import { fetchPublicOffers, fetchPublicPartners } from "@/lib/clubApi";
import { fetchActivePrograms, DEFAULT_PROGRAM } from "@/lib/programs";
import { usePageMeta } from "@/lib/usePageMeta";
import { JoinForm } from "@/components/club/JoinForm";
import Reviews from "@/components/sections/Reviews";
import Footer from "@/components/sections/Footer";

const LEGACY_ANCHORS = ["reservation", "pricing", "process", "faq", "services", "guarantee"];

const Section = ({ id, kicker, title, children, className = "" }) => (
  <section id={id} data-testid={`club-${id}`} className={`py-16 lg:py-24 border-t border-brand-border ${className}`}>
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
      {kicker && <div className="text-xs uppercase tracking-[0.3em] text-brand-accent">{kicker}</div>}
      {title && <h2 className="mt-3 font-display font-black text-3xl lg:text-5xl tracking-tight leading-[1.05]">{title}</h2>}
      <div className="mt-8">{children}</div>
    </div>
  </section>
);

const CTA = ({ to = "/#vstup", children, secondary, testId }) => (
  <a href={to} data-testid={testId}
    className={`inline-flex items-center justify-center h-12 px-6 font-semibold transition-colors ${secondary ? "border border-brand-border text-white hover:border-brand-accent hover:text-brand-accent" : "bg-brand-accent text-black hover:bg-brand-accentHover"}`}>
    {children}
  </a>
);

const SERVICES = [
  { title: "Kompletní detailing", desc: "Ruční mytí, dekontaminace, strojové leštění laku a čištění interiéru — vše v jednom.", items: ["Ruční mytí exteriéru", "Dekontaminace laku", "Strojové leštění laku", "Čištění a tepování interiéru", "Renovace světlometů", "Čištění motorového prostoru"] },
  { title: "Mytí vozidel", desc: "Šetrné ruční mytí pro pravidelnou péči o vaše auto.", items: ["Ruční mytí exteriéru", "Mytí a péče o interiér", "Ošetření plastů a pneumatik", "Mytí oken a zrcátek"] },
  { title: "Čištění interiéru", desc: "Hloubkové čištění kabiny a péče o materiály.", items: ["Suché čištění", "Tepování sedaček a koberců", "Ozonizace — odstranění pachů", "Péče a čištění kůže"] },
  { title: "Ochrana laku a keramika", desc: "Dlouhodobá ochrana laku, skel a detailů.", items: ["Sealanty a vosky", "Keramické povlaky", "Ochrana skel", "Ochrana plastů"] },
];

const FAQ = [
  ["Kolik stojí členství?", "Pro prvních 500 členů je členství zdarma — bez předplatného a bez platební karty. Po naplnění 500 členů se pro nové zájemce aktivuje cena členství; stávající členové budou o podmínkách informováni předem. Platíte jen objednané služby."],
  ["Jak objednám péči?", "Vyberete nabídku a pobočku, odešlete poptávku s kontaktem. Ozveme se vám a domluvíme termín, který vám vyhovuje."],
  ["Je odeslání formuláře potvrzená rezervace?", "Ne — formulář je poptávka. Termín potvrdíme telefonicky nebo e-mailem."],
  ["Jaké výhody mám jako člen?", "Aktuálně platné výhody a jejich podmínky najdete v sekci Výhody klubu a ve svém účtu. Zobrazujeme jen výhody, které mají vyplněné podmínky."],
  ["Jak se přihlásím do účtu?", "Bez hesla: na stránce Můj účet zadáte e-mail a pošleme vám jednorázový přihlašovací odkaz platný 15 minut."],
];

export default function ClubHome() {
  usePageMeta({
    title: "CARSPA CLUB — Ruční detailing Praha | Členství zdarma",
    description: "Vstupte do CARSPA CLUB zdarma (prvních 500 členů). Prémiový ruční detailing v Čestlicích a Butovicích, výhody při každé návštěvě, členská karta a odměny za body.",
    path: "/",
  });
  const [offers, setOffers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [programs, setPrograms] = useState([DEFAULT_PROGRAM]);
  const [open, setOpen] = useState(0);
  const { hash } = useLocation();
  const navigate = useNavigate();

  // Keep existing ad links working: /#reservation etc. -> /akce/detailing#…
  useEffect(() => {
    const h = hash.replace("#", "");
    if (LEGACY_ANCHORS.includes(h)) navigate(`/akce/detailing#${h}`, { replace: true });
    else if (h) document.getElementById(h)?.scrollIntoView({ behavior: "smooth" });
  }, [hash, navigate]);

  useEffect(() => {
    fetchPublicOffers().then(setOffers).catch(() => {});
    fetchPublicPartners().then(setPartners).catch(() => {});
    fetchActivePrograms().then((d) => {
      const seen = new Set();
      const list = Object.values(d.by_location).filter((p) => p && !seen.has(p.id) && seen.add(p.id));
      setPrograms(list);
    }).catch(() => {});
  }, []);

  return (
    <div className="bg-brand-bg text-white font-body" data-testid="club-home">
      <header className="sticky top-0 z-40 bg-brand-bg/90 backdrop-blur border-b border-brand-border">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="font-display font-black tracking-tight flex items-center gap-2" data-testid="club-logo">
            <span className="w-2 h-2 bg-brand-accent" /> CARSPA CLUB
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm text-white/80">
            <a href="#vyhody" className="hover:text-brand-accent">Výhody klubu</a>
            <a href="#sluzby" className="hover:text-brand-accent">Služby</a>
            <a href="#pobocky" className="hover:text-brand-accent">Pobočky</a>
            <a href="#dotazy" className="hover:text-brand-accent">Časté dotazy</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/muj-ucet" data-testid="nav-account" className="h-10 px-3 inline-flex items-center text-sm border border-brand-border hover:border-brand-accent hover:text-brand-accent">Můj účet</Link>
            <a href="#vstup" data-testid="nav-join" className="h-10 px-4 inline-flex items-center text-sm font-semibold bg-brand-accent text-black hover:bg-brand-accentHover">Vstoupit zdarma</a>
          </div>
        </div>
      </header>

      {/* 1 — hero */}
      <section className="relative overflow-hidden" data-testid="club-hero">
        <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{ backgroundImage: `url(${SITE.process[2].img})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-bg/40 via-brand-bg/70 to-brand-bg" />
        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-10 pt-16 pb-14 lg:pt-28 lg:pb-24 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.35em] text-brand-accent">CARSPA CLUB</div>
            <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              Vaše auto v péči. <span className="italic font-light">Výhody při každé návštěvě.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-white/80 max-w-xl">
              Přidejte se zdarma do Carspa Clubu. Objevte členské nabídky a objednejte péči o své auto v Čestlicích nebo Butovicích.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <CTA to="#vstup" testId="hero-join">Vstoupit zdarma</CTA>
              <CTA to="#sluzby" secondary testId="hero-offer">Prohlédnout nabídku</CTA>
            </div>
            <div className="mt-4 text-sm text-brand-muted" data-testid="hero-free-note">Členství je <b className="text-white">zatím zdarma pro prvních 500 členů</b> — poté se aktivuje cena. Bez předplatného.</div>
          </div>
          <div className="lg:col-span-5" id="vstup">
            <JoinForm compact />
          </div>
        </div>
      </section>

      {/* 2 — benefits */}
      <Section id="vyhody" kicker="Členské výhody" title="Co členství přináší">
        {offers.length === 0 ? (
          <div className="bg-brand-surface border border-brand-border p-6 text-brand-muted" data-testid="offers-empty">
            Podmínky prvních členských výhod právě připravujeme. Jako člen se o nich dozvíte první — ve svém účtu a e-mailem, pokud si odběr zapnete.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="offers-list">
            {offers.map((o) => (
              <div key={o.id} className="bg-brand-surface border border-brand-border p-6" data-testid={`offer-${o.key}`}>
                <div className="font-display font-black text-xl">{o.title}</div>
                <p className="mt-2 text-sm text-white/80">{o.description}</p>
                <p className="mt-3 text-xs text-brand-muted whitespace-pre-line">Podmínky: {o.conditions}</p>
                {o.valid_to && <p className="mt-1 text-xs text-brand-muted">Platí do {o.valid_to}</p>}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* 2b — partner benefits teaser (public: names + discount only; codes unlocked in member area) */}
      {partners.length > 0 && (
        <Section id="partneri" kicker="Partnerské výhody" title="Sleva u našich partnerů — jen pro členy">
          <p className="text-sm text-brand-muted -mt-2 mb-5 max-w-2xl">Členové CARSPA CLUB získávají slevy u vybraných partnerů. <b className="text-white">Konkrétní slevový kód se odemkne po přihlášení</b> do členského účtu (podle vaší karty).</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="partners-teaser">
            {partners.map((p) => (
              <div key={p.id} className="bg-brand-surface border border-brand-border p-5 flex items-center gap-4" data-testid={`partner-teaser-${p.id}`}>
                {p.logo ? <img src={p.logo} alt={p.name} className="w-12 h-12 object-contain bg-white/5 border border-brand-border p-1 shrink-0" /> : <div className="w-12 h-12 grid place-items-center bg-brand-bg border border-brand-border text-brand-accent font-display font-black shrink-0">{p.name.slice(0, 1)}</div>}
                <div className="min-w-0">
                  <div className="font-semibold truncate">{p.name}</div>
                  <div className="mt-1 flex items-center gap-2">
                    {p.discount && <span className="text-xs font-bold text-black bg-brand-accent px-2 py-0.5">{p.discount}</span>}
                    {p.category && <span className="text-[10px] uppercase tracking-wide text-brand-muted">{p.category}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            <CTA to="#vstup" testId="partners-join">Vstoupit zdarma a odemknout</CTA>
            <CTA to="/muj-ucet" secondary testId="partners-login">Už jsem člen — přihlásit se</CTA>
          </div>
        </Section>
      )}

      <Section id="prvni-navsteva" kicker={programs.length > 1 ? "Akční programy na pobočkách" : "Akční program · nabídka první návštěvy"}
        title={programs.length === 1 ? <>{programs[0].title} za <span className="text-brand-accent">{czk(programs[0].price)}</span></> : "Zvýhodněný program pro každou pobočku"}>
        {programs.length === 0 ? (
          <div className="bg-brand-surface border border-brand-border p-6 text-brand-muted" data-testid="programs-empty">Akční program právě připravujeme — členové se o něm dozvědí první e-mailem a ve svém účtu.</div>
        ) : (
          <div className={`grid gap-6 ${programs.length > 1 ? "lg:grid-cols-2" : "lg:grid-cols-12"}`} data-testid="programs-list">
            {programs.map((p, idx) => (
              <React.Fragment key={p.id || idx}>
                <div className={`bg-brand-surface border border-brand-border p-6 lg:p-8 ${programs.length > 1 ? "" : "lg:col-span-7"}`} data-testid={`program-card-${p.location}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.3em] text-brand-accent" data-testid={`program-card-location-${p.location}`}>{p.location_label}{p.valid_to ? ` · platí do ${new Date(p.valid_to).toLocaleDateString("cs-CZ")}` : ""}</div>
                      <div className="mt-2 font-display font-black text-2xl">{p.title}</div>
                      {p.subtitle && <p className="mt-2 text-sm text-white/80 max-w-xl">{p.subtitle}</p>}
                    </div>
                    <div className="text-right">
                      {p.regular_price_effective > p.price && <div className="text-brand-muted line-through font-display">{czk(p.regular_price_effective)}</div>}
                      <div className="font-display font-black text-4xl text-brand-accent leading-none" data-testid={`program-card-price-${p.location}`}>{czk(p.price)}</div>
                      {p.savings > 0 && <div className="mt-1 text-xs text-brand-accent">Ušetříte {czk(p.savings)} (−{p.discount_pct} %)</div>}
                    </div>
                  </div>
                  {p.items?.length > 0 && (
                    <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                      {p.items.map((it) => (
                        <li key={it.label} className="flex justify-between gap-3 text-sm text-white/90"><span className="flex gap-2"><Check size={16} className="text-brand-accent shrink-0 mt-0.5" />{it.label}</span>{it.price > 0 && <span className="text-brand-muted line-through shrink-0">{czk(it.price)}</span>}</li>
                      ))}
                    </ul>
                  )}
                  <p className="mt-6 text-sm text-brand-muted">
                    Cena platí pro standardní osobní vůz. <b className="text-white">SUV / velký vůz +{SITE.suvSurchargePct} %</b> na celou objednávku — přesnou částku vidíte ve formuláři před odesláním.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <CTA to={`/akce/detailing?pobocka=${p.location === "all" ? "cestlice" : p.location}#reservation`} testId={idx === 0 ? "first-visit-cta" : `program-cta-${p.location}`}>Objednat program</CTA>
                    {idx === 0 && <CTA to="/akce/detailing" secondary testId="first-visit-detail">Detail nabídky</CTA>}
                  </div>
                </div>
                {programs.length === 1 && (
                  <div className="lg:col-span-5 relative min-h-[240px] overflow-hidden">
                    <img src={SITE.process[0].img} alt={SITE.process[0].alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </Section>

      {/* 4 — services presentation (no prices) with the half-price detailing promo up front */}
      <Section id="sluzby" kicker="Naše služby" title="Kompletní péče o vaše auto">
        <div className="mb-8 bg-brand-accent text-black p-6 lg:p-8 flex flex-wrap items-center justify-between gap-5" data-testid="detailing-promo">
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.3em] font-black">Akce právě teď</div>
            <div className="mt-2 font-display font-black text-2xl lg:text-4xl leading-[1.05]">Kompletní detailing za půlku</div>
            <p className="mt-2 text-sm lg:text-base font-medium opacity-80">Ruční mytí, dekontaminace, strojové leštění laku a péče o interiér — teď se slevou <b>50 %</b>. Počet termínů je omezený.</p>
          </div>
          <a href="/akce/detailing#reservation" data-testid="detailing-promo-cta" className="h-12 px-7 inline-flex items-center font-bold bg-black text-brand-accent whitespace-nowrap hover:opacity-90 transition-opacity">Objednat akci →</a>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          {SERVICES.map((c) => (
            <div key={c.title} className="bg-brand-surface border border-brand-border p-6" data-testid={`category-${c.title}`}>
              <div className="font-display font-black text-xl">{c.title}</div>
              <p className="mt-1 text-sm text-brand-muted">{c.desc}</p>
              <ul className="mt-4 grid sm:grid-cols-2 gap-2 text-sm">
                {c.items.map((it) => (
                  <li key={it} className="flex gap-2 text-white/90"><Check size={15} className="text-brand-accent shrink-0 mt-0.5" />{it}</li>
                ))}
              </ul>
              <a href="/akce/detailing#reservation" data-testid={`category-cta-${c.title}`}
                className="mt-5 inline-flex h-10 px-4 items-center text-sm font-semibold border border-brand-border hover:border-brand-accent hover:text-brand-accent">
                Poptat službu
              </a>
            </div>
          ))}

          <div className="bg-brand-surface border border-brand-accent/40 p-6 md:col-span-2" data-testid="category-partners">
            <div className="font-display font-black text-xl">Slevy u partnerů</div>
            <p className="mt-1 text-sm text-brand-muted">Jako člen klubu získáváte slevy u vybraných partnerů — od gastra po autoslužby. Konkrétní slevový kód se odemkne ve vašem účtu podle členské karty.</p>
            <div className="mt-5 flex flex-wrap gap-2">
              <a href="#partneri" className="inline-flex h-10 px-4 items-center text-sm font-semibold border border-brand-border hover:border-brand-accent hover:text-brand-accent">Zobrazit partnery</a>
              <a href="#vstup" className="inline-flex h-10 px-4 items-center text-sm font-semibold bg-brand-accent text-black hover:bg-brand-accentHover">Vstoupit zdarma a odemknout</a>
            </div>
          </div>
        </div>
        <p className="mt-4 text-xs text-brand-muted">Ceny stanovujeme podle typu vozu a rozsahu — rádi vám připravíme nezávaznou nabídku. Aktuální zvýhodněné ceny najdete v <a href="#prvni-navsteva" className="text-brand-accent">akčním programu</a>.</p>
      </Section>

      {/* 5 — how it works */}
      <Section id="jak" kicker="Jak klub funguje" title="Čtyři jednoduché kroky">
        <ol className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {["Vstoupíte zdarma.", "Vyberete nabídku a pobočku.", "Přijedete na domluvený termín.", "Další péči a výhody najdete ve svém účtu."].map((t, i) => (
            <li key={t} className="bg-brand-surface border border-brand-border p-6"><div className="font-display font-black text-3xl text-brand-accent">0{i + 1}</div><div className="mt-3 text-white/90">{t}</div></li>
          ))}
        </ol>
      </Section>

      {/* 6 — trust */}
      <div id="duvera"><Reviews /></div>

      {/* 7 — branches */}
      <Section id="pobocky" kicker="Pobočky" title="Čestlice a Butovice">
        <div className="grid md:grid-cols-2 gap-3">
          {SITE.locations.map((l) => (
            <div key={l.key} className="bg-brand-surface border border-brand-border p-6" data-testid={`branch-${l.key}`}>
              <div className="font-display font-black text-xl">{l.name}</div>
              <div className="mt-3 space-y-2 text-sm text-white/85">
                <div className="flex gap-2"><MapPin size={15} className="text-brand-accent mt-0.5" /><span>{l.address}{l.hint && <span className="block text-brand-accent text-xs">{l.hint}</span>}</span></div>
                <div className="flex gap-2"><Clock size={15} className="text-brand-accent mt-0.5" />{l.hours}</div>
                <div className="flex gap-2"><Phone size={15} className="text-brand-accent mt-0.5" /><a href={`tel:${l.phone.replace(/\s/g, "")}`} className="hover:text-brand-accent" data-testid={`branch-phone-${l.key}`}>{l.phone}</a> <span className="text-brand-muted">· WhatsApp {SITE.whatsappNumber}</span></div>
              </div>
              <div className="mt-5 flex gap-2">
                <a href={`https://www.google.com/maps/search/${encodeURIComponent(l.address)}`} target="_blank" rel="noreferrer" className="h-10 px-4 inline-flex items-center text-sm border border-brand-border hover:border-brand-accent">Navigovat</a>
                <a href="/akce/detailing#reservation" className="h-10 px-4 inline-flex items-center text-sm font-semibold bg-brand-accent text-black">Objednat</a>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* 8 — FAQ */}
      <Section id="dotazy" kicker="Časté dotazy" title="Na co se ptáte nejčastěji">
        <div className="divide-y divide-brand-border border-y border-brand-border">
          {FAQ.map(([q, a], i) => (
            <div key={q}>
              <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full py-4 flex justify-between gap-4 text-left" data-testid={`faq-${i}`}>
                <span className="font-semibold">{q}</span><ChevronDown size={18} className={`shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && <p className="pb-4 text-sm text-brand-muted">{a}</p>}
            </div>
          ))}
        </div>
        <div className="mt-10 bg-brand-accent text-black p-6 lg:p-8 flex flex-wrap items-center justify-between gap-4">
          <div><div className="font-display font-black text-2xl">Vstupte do klubu zdarma — pro prvních 500 členů.</div><div className="text-sm opacity-80">Jméno, e-mail, pobočka — nic víc. Poté se aktivuje cena členství.</div></div>
          <a href="#vstup" data-testid="final-join" className="h-12 px-6 inline-flex items-center font-bold bg-black text-brand-accent">Vstoupit zdarma</a>
        </div>
      </Section>
      <Footer />
    </div>
  );
}
